<template>
  <Modal
    content-class
    :modal-style="modalStyle"
    :title="modalTitle"
    @close="close"
  >
    <div class="modal-content">
      <div v-if="isSocial">
        <AccountTypeRadioSelector
          v-model="stream.social_only_verified"
          :disabled="disabled"
          :medium="source.field.toLowerCase()"
        />
      </div>

      <div v-if="!stream.disableLocations && !isSocial">
        <LocationCheckboxSelector
          v-model="selectedLocations"
          v-model:valid="hasValidLocations"
          :disabled="disabled"
          :medium="source.field.toLowerCase()"
          :organisation="organisation"
          required
        />
      </div>

      <div>
        <CategoryCheckboxSelector
          v-model="categoriesSelected"
          v-model:valid="hasValidCategories"
          :disabled="disabled"
          :medium="source.field.toLowerCase()"
          required
        />
      </div>

      <div v-if="showTierFilters && !isSocial">
        <TierRadioSelector
          :disabled="disabled"
          :medium="source.field.toLowerCase()"
          :model-value="tierValue"
          @update:model-value="setTierValue"
        />
      </div>

      <div v-if="showCALDFilter">
        <CaldRadioSelector
          :disabled="disabled"
          :is-admin="isAdmin"
          :medium="source.field.toLowerCase()"
          :model-value="caldValue"
          @update:model-value="setCaldValue"
        />
      </div>

      <div v-if="showInternationalMaxFilter">
        <InternationalMaxRadioSelector
          v-model="stream.international_max"
          :disabled="disabled"
          :is-admin="isAdmin"
          :medium="source.field.toLowerCase()"
        />
      </div>

      <div
        v-if="isSocial"
        class="q-px-md q-pt-md"
      >
        <div class="q-item__label">
          {{ $t("modals.filter_mentions_modal.impact_threshold_label") }}
        </div>
        <QSlider
          v-model="stream.social_impact_threshold"
          class="q-mt-xl q-px-sm"
          color="ds-purple-5"
          :disable="disabled"
          label
          label-always
          :max="90"
          :min="0"
          snap
          :step="10"
        />
      </div>
    </div>

    <template #buttons>
      <div class="modal-buttons">
        <div>
          <Checkbox
            v-if="!isSocial"
            v-model="shouldApplyToAllSources"
            class="small"
            :label="$t('modals.filter_mentions_modal.toggle_apply_all_label')"
            new-design
            style="font-size: 13px"
          />
        </div>
        <div class="modal-buttons__actions">
          <BaseButton
            v-if="$isDesktop"
            :label="$t('global.cancel')"
            new-design
            @click.prevent="close"
          />
          <BaseButton
            color="primary"
            :disabled="!isValid"
            :label="$t('global.apply')"
            new-design
            @click="save"
          />
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup>
import { useQuasar } from "quasar";
import { computed, inject, ref } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import { BaseButton } from "shared/components/base";
import Modal from "shared/components/core/modals/Modal.vue";
import CategoryCheckboxSelector from "shared/components/selectors/checkbox/CategoryCheckboxSelector.vue";
import LocationCheckboxSelector from "shared/components/selectors/checkbox/LocationCheckboxSelector.vue";
import AccountTypeRadioSelector from "shared/components/selectors/radio/AccountTypeRadioSelector.vue";
import CaldRadioSelector from "shared/components/selectors/radio/CaldRadioSelector.vue";
import InternationalMaxRadioSelector from "shared/components/selectors/radio/InternationalMaxRadioSelector.vue";
import TierRadioSelector from "shared/components/selectors/radio/TierRadioSelector.vue";
import useLocations from "shared/composables/useLocations";
import { useStreamsStore } from "shared/stores/streams";

import ModalService from "./ModalService";

const props = defineProps({
  stream: {
    type: Object,
    required: true,
  },
  source: {
    type: Object,
    required: true,
  },
  allowedSources: {
    type: Array,
    default: () => [],
  },
  showTierFilters: {
    type: Boolean,
  },
  isAdmin: {
    type: Boolean,
  },
  organisation: {
    type: [Object, null],
    default: null,
  },
  showCALDFilter: {
    type: Boolean,
  },
  showInternationalMaxFilter: {
    type: Boolean,
  },
  disabled: {
    type: Boolean,
  },
});

const emit = defineEmits([
  "refresh",
  "update:filters",
  "update:cald",
  "update:tier",
]);

const $q = useQuasar();

const filters = ref([...props.stream.filters]);

function hasFilter(targetType, id, source) {
  const index = filters.value.findIndex(
    (filter) =>
      filter.target_type === targetType &&
      filter.scope === (source || props.source.field) &&
      filter.target_id === id
  );

  return index === -1 ? null : { index };
}

function addFilter(targetType, id, source) {
  if (hasFilter(targetType, id, source)) return;

  const filter = { id };

  if (targetType === "Location") {
    filter.visible = true;
  }

  filters.value.push({
    target_type: targetType,
    scope: source || props.source.field,
    target_id: id,
    action: "exclude",
    filter,
  });
}

const categoriesSelected = computed({
  get() {
    const items = filters.value.filter(
      (item) =>
        item.target_type === "Category" && item.scope === props.source.field
    );

    return items.map((item) => item.target_id);
  },
  set(selected = []) {
    filters.value = filters.value.filter(
      (item) =>
        !(item.target_type === "Category" && item.scope === props.source.field)
    );

    selected.forEach((id) => {
      addFilter("Category", id);
    });
  },
});

function caldValueDependingOnSourceField() {
  if (props.showCALDFilter) {
    if (props.source.field === "online")
      return props.stream.include_online_cald_sources;

    return props.stream.include_print_cald_sources;
  }

  return null;
}

const caldValue = ref(caldValueDependingOnSourceField());
const tierValue = ref(props.stream[`${props.source.field}_tier`]);

const isSocial = computed(() => props.source.field === "social");
const $isDesktop = inject("isDesktop");

const modalTitle = computed(() =>
  getLocaleText("modals.filter_mentions_modal.title", {
    sourceLabel: props.source.label,
  })
);

const modalStyle = computed(() => {
  if ($isDesktop) {
    return { width: "460px" };
  }

  return {};
});

const { locations, international, getSelectedLocations, topLocations } =
  useLocations(props.organisation);

function getExcludedLocationsIds(selected = []) {
  const excludedLocationIds = [];
  const topLocationIds = topLocations.value.map((location) => location.id);

  locations.value.forEach((location) => {
    if (
      location.id === international.value.id ||
      selected.includes(location.id)
    )
      return;

    // don't exclude children if the parent is excluded unless it's a top location
    const isTopLocation = topLocationIds.includes(location.id);
    if (
      !isTopLocation &&
      location.parent_id &&
      !selected.includes(location.parent_id)
    )
      return;

    excludedLocationIds.push(location.id);
  });

  return excludedLocationIds;
}

const selectedLocations = computed({
  get() {
    const filteredLocations = filters.value
      .filter(
        (item) =>
          item.target_type === "Location" && item.scope === props.source.field
      )
      .map((item) => item.target_id);

    return getSelectedLocations(filteredLocations).map(
      (location) => location.id
    );
  },
  set(selected = []) {
    filters.value = filters.value.filter(
      (item) =>
        !(item.target_type === "Location" && item.scope === props.source.field)
    );

    const notSelectedIds = getExcludedLocationsIds(selected);

    notSelectedIds.forEach((id) => {
      addFilter("Location", id);
    });
  },
});

const hasValidCategories = ref(false);

const hasValidLocations = ref(false);

const isValid = computed(
  () =>
    hasValidCategories.value &&
    (hasValidLocations.value || props.stream.disableLocations || isSocial.value)
);

function setCaldValue(value, source) {
  caldValue.value = value;

  emit("update:cald", {
    value,
    source: source || props.source.field,
  });
}

function setTierValue(value, source) {
  tierValue.value = value;

  emit("update:tier", {
    value,
    source: source || props.source.field,
  });
}

function close(emitRefresh = true) {
  if (emitRefresh) {
    emit("refresh", props.stream);
  }

  ModalService.close("FilterMentionsModal");
}

const shouldApplyToAllSources = ref(false);

function applyToAllApplicable() {
  if (!shouldApplyToAllSources.value || isSocial.value) return;

  const onlineAndPrint = ["online", "print"];

  // Get all available sources except social
  const allSourcesExceptSocial = props.allowedSources.filter(
    (source) => source !== props.source.field && source !== "social"
  );

  // Apply Location Filters
  const clearFilters = (type, source) => {
    filters.value = filters.value.filter(
      (item) => !(item.target_type === type && item.scope === source)
    );
  };

  const excludedLocations = getExcludedLocationsIds(selectedLocations.value);

  allSourcesExceptSocial.forEach((source) => {
    clearFilters("Location", source);

    excludedLocations.forEach((id) => {
      addFilter("Location", id, source);
    });
  });

  const excludedCategories = categoriesSelected.value;

  // Apply Category Filters
  allSourcesExceptSocial.forEach((source) => {
    clearFilters("Category", source);

    excludedCategories.forEach((id) => {
      addFilter("Category", id, source);
    });
  });

  // Apply Tier filter to all sources except social
  if (props.showTierFilters) {
    allSourcesExceptSocial.forEach((source) => {
      setTierValue(tierValue.value, source);
    });
  }

  // Apply CALD filter to online & print
  if (props.showCALDFilter) {
    onlineAndPrint.forEach((source) => {
      setCaldValue(caldValue.value, source);
    });
  }
}

async function save() {
  if (!isValid.value) return;

  if (shouldApplyToAllSources.value && !isSocial.value) {
    await applyToAllApplicable();
  }

  if (props.stream.id && !props.isAdmin) {
    $q.loading.show({ delay: 0 });

    try {
      await useStreamsStore().updateStreamFilters({
        id: props.stream.id,
        filters: filters.value.map((filter) => ({
          target_id: filter.target_id,
          target_type: filter.target_type,
          scope: filter.scope,
          action: filter.action,
        })),
      });

      await useStreamsStore().updateStream({
        id: props.stream.id,
        params: {
          social_impact_threshold: props.stream.social_impact_threshold,
          social_only_verified: props.stream.social_only_verified,
        },
      });

      emit("update:filters", filters.value);
    } finally {
      $q.loading.hide();
    }
  } else {
    emit("update:filters", filters.value);
  }

  close(false);
}
</script>

<style lang="scss" scoped>
:deep(.q-item__label) {
  font-weight: bold;
}

.modal-content {
  max-height: 600px;
  overflow-y: auto;
}

.modal-buttons {
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  padding: 0 8px;

  &__actions {
    display: flex;
    gap: var(--s-spacing-md);
  }
}
</style>
